import memoize from "lodash-es/memoize";

import { FeaturesFlags } from "@/store/features/features";
import { Plan } from "@/api";

import { UserSlice } from "../user";
import { FeatureLimitFeatures, FeatureLimits } from "./limits";

export type { FeaturesFlags } from "./features";

type ENV = "dev" | "beta" | "prod";

export type FeatureSlice = {
  features: FeaturesFlags;
  hasFeature: (name: keyof FeaturesFlags) => boolean;
  getFeatureLimit: (feature: keyof FeatureLimitFeatures) => FeatureLimitFeatures[keyof FeatureLimitFeatures];
  // Is running either on Local or Dev
  isDev: boolean;
  isBeta: boolean;
  isProd: boolean;
  isAdmin: boolean;
};

export function createFeaturesSlice(initialState: FeaturesFlags | undefined, userSlice: UserSlice): FeatureSlice {
  // Not a signal as we expect this to never change during client execution
  const features = initialState || {};

  return {
    features,
    hasFeature: hasFeature(features),
    getFeatureLimit: getFeatureLimit(userSlice),
    isDev: isENV(features, "dev"),
    isBeta: isENV(features, "beta"),
    isProd: isENV(features, "prod"),
    isAdmin: hasFeature(features)("general.admin"),
  };
}

function hasFeature(features: FeaturesFlags): (name: keyof FeaturesFlags) => boolean {
  return name => !!features[name];
}

function isENV(features: FeaturesFlags, env: ENV): boolean {
  return !!features[`general.${env}`];
}

/**
 * Gets the feature limit based on the user's plan.
 * @param userSlice - The user slice containing user information.
 * @returns A function that takes a feature key and returns the corresponding feature limit.
 */
function getFeatureLimit(
  userSlice: UserSlice,
): (feature: keyof FeatureLimitFeatures) => FeatureLimitFeatures[keyof FeatureLimitFeatures] {
  return memoize((feature: keyof FeatureLimitFeatures) => {
    const plan = userSlice.isPro() ? Plan.PRO : Plan.FREE;
    return FeatureLimits[feature][plan];
  });
}
